import { useAccount, useMsal } from '@azure/msal-react';
import { Grid } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Form, Formik } from 'formik';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';

import { ICreateCheckRequest } from '@modules/accounting/CheckerMenu/interfaces';
import { IGapLoanPaidOffRequestInformation } from '@modules/admin/Tools/shared/interfaces';
import { getOptions, gridDateTimeFormatter, mapGridIndexes, queryParams } from '@shared/commonFunctions';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Modal } from '@shared/components/modals/Modal';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';

import { DealContext } from '../../DealContext/DealContext';
import { CheckRequestType, ILenderResponse, IProductsFetch } from '../../interfaces';
import { CheckRequestForm, IContactPCRF } from '../CheckRequestForm';
import { checkRequestSchema } from './CheckRequestConstants';
import { productCheckRequestSchema } from './ProductCheckRequestConstant';
import { submitCheckRequest } from './request/CheckRequestApi';
import { StyleOverride } from './StyleOverride';

export interface IDealInfo {
  dealId: string;
  assetTag?: string;
  lenderResponse?: ILenderResponse;
  gapLoanPaidOffRequestInformation?: IGapLoanPaidOffRequestInformation;
}

const titleCheckRequestColumns: GridColDef[] = [
  dataGridCol('numericId', {
    headerName: 'Check Id',
  }),
  dataGridCol('dealNumericId', { headerName: 'Deal Id' }),
  dataGridCol('requestedDate', {
    headerName: 'Req Date',
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('createdBy', { headerName: 'Req By' }),
  dataGridCol('customerName', { headerName: 'Customer' }),
  dataGridCol('loanConsultant', {
    headerName: 'LC',
  }),
  dataGridCol('payeeName', {
    headerName: 'Payee',
  }),
  dataGridCol('amountRequested', {
    headerName: 'Amt Req',
    ...gridCurrencyCell,
  }),
  dataGridCol('checkStatus', {
    headerName: 'Status',
  }),
  dataGridCol('reason'),
  dataGridCol('assetTag', {
    headerName: 'Asset Desc',
  }),
];

const productCheckRequestColumns: GridColDef[] = [
  dataGridCol('requestedDate', {
    headerName: 'Req Date',
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('createdBy', {
    headerName: 'Req By',
  }),
  dataGridCol('provider'),
  dataGridCol('productType', {
    headerName: 'Type',
  }),
  dataGridCol('payeeName', {
    headerName: 'Payee',
  }),
  dataGridCol('customerAmountRefund', {
    headerName: 'Cust Amt Req',
    ...gridCurrencyCell,
  }),
  dataGridCol('sellerAmountRefund', {
    headerName: 'Seller Amt Req',
    ...gridCurrencyCell,
  }),
  dataGridCol('checkStatus', {
    headerName: 'Status',
  }),
  dataGridCol('reason'),
  dataGridCol('assetTag', {
    headerName: 'Asset Desc',
  }),
].map((column) => ({ ...column, flex: 1 }));

const getPayeeOptionList = (dealInfo?: IDealInfo, contactDetail?: IContactPCRF) => {
  const lenderDetail = dealInfo?.lenderResponse;
  const crrentAddress = contactDetail?.addresses?.find((address) => address?.isCurrent);
  if (!!lenderDetail && !!contactDetail) {
    const optionList = [];
    if (lenderDetail?.payeeName && lenderDetail?.payeeAddress) {
      optionList.push({
        label: `${lenderDetail?.payeeName || ''} ${lenderDetail?.payeeAddress || ''}`,
        value: `${lenderDetail?.payeeName || ''} ${lenderDetail?.payeeAddress || ''}`,
      });
    }
    const paidOffInfo = dealInfo?.gapLoanPaidOffRequestInformation;

    if (paidOffInfo?.payeeName && paidOffInfo?.payeeAddress) {
      const formatted = `${paidOffInfo.payeeName} ${paidOffInfo.payeeAddress}`;

      optionList.push({
        label: formatted,
        value: formatted,
      });
    }
    optionList.push({
      label: `${contactDetail?.firstName} ${contactDetail?.lastName} ${crrentAddress?.streetLine1 || ''} ${crrentAddress?.unit || ''} ${
        crrentAddress?.city || ''
      } ${crrentAddress?.state || ''} ${crrentAddress?.zip || ''}`,
      value: `${contactDetail?.firstName} ${contactDetail?.lastName} ${crrentAddress?.streetLine1 || ''} ${crrentAddress?.unit || ''} ${
        crrentAddress?.city || ''
      } ${crrentAddress?.state || ''} ${crrentAddress?.zip || ''}`,
    });
    return optionList;
  }
  return [];
};

interface ICheckRequestModal {
  checkRequestModalPosition: number;
  handleClose: () => void;
  checkRequestType: CheckRequestType;
  dealId: string;
  dealData?: IProductsFetch;
}

interface IReasonResponse {
  id: string;
  reason: string;
}

export const CheckRequestModal = ({
  checkRequestModalPosition,
  handleClose,
  checkRequestType,
  dealId,
  dealData,
}: ICheckRequestModal) => {
  const { contextValue } = useContext(DealContext);
  const { accounts } = useMsal();
  const dispatch = useDispatch();
  const [workFlowError, setWorkFlowError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showisApprovalRequiredAlert, setShowisApprovalRequiredAlert] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<ICreateCheckRequest | null>(null);


  const account = useAccount(accounts[0] || {});
  const userName = account?.name;

  const { data: checkReqList, mutate } = useSWR(
    `/${SERVICES.ACCOUNTING}/checkrequest/deal/${dealId}?checkRequestType=${checkRequestType}`
  );
  const dealResponse = useSWR(`/${SERVICES.DEAL}/deals/${dealId}`);
  const contact = useSWR(`/${SERVICES.CONTACT}/contacts/${queryParams().id}`);
  const { data: reasonList = [] } = useSWR<IReasonResponse[]>(`/${SERVICES.ACCOUNTING}/cancellationreason`);

  const dealInfo = useMemo(
    () => ({
      dealId: dealResponse?.data?.numericId,
      assetTag: dealResponse?.data?.assetTag,
      lenderResponse: {
        ...dealResponse?.data?.lenderResponse,
        payeeName: dealResponse?.data?.productCheckRequestPayee?.payeeName,
        payeeAddress: dealResponse?.data?.productCheckRequestPayee?.payeeAddress,
      },
      gapLoanPaidOffRequestInformation: dealResponse?.data?.productCheckRequestPayee?.gapLoanPaidOffRequestInformation,
      isBuyBack: dealResponse?.data?.isBuyBack
      
    }),
    [dealResponse]
  );
  const payeeList = useMemo(() => getPayeeOptionList(dealInfo, contact?.data), [dealInfo?.lenderResponse, contact?.data]);

  const workFlowErrorCb = (errorMsg?: string) => {
    if (errorMsg) {
      setWorkFlowError(errorMsg);
    }
  };

  const checkRequestConfig = useMemo(
    () => ({
      [CheckRequestType.CheckRequest]: {
        validation: checkRequestSchema,
        initialValues: {
          dealId,
          assetTag: '',
          payeeName: '',
          reason: '',
          relatedDeal: '',
          loanConsultant: '',
          checkRequestType: Number(CheckRequestType.CheckRequest),
          requestedBy: '',
          amountRequested: null,
          customerName: `${contact?.data?.firstName} ${contact?.data?.lastName}`,
          requestComment: '',
          includeInBatch: true,
        },
      },
      [CheckRequestType.ProductCancelCheck]: {
        validation: productCheckRequestSchema,
        initialValues: {
          dealId,
          assetTag: '',
          productType: '',
          reason: '',
          payeeName: payeeList[0]?.value || null,
          provider: '',
          relatedDeal: dealInfo?.dealId || null,
          checkRequestType: CheckRequestType.ProductCancelCheck,
          requestedBy: '',
          amountRequested: null,
          lenderCode: dealInfo?.lenderResponse?.lenderName,
          sellerAmountRefund: null,
          customerAmountRefund: null,
          cancelledDate: new Date(),
          achRefNumber: '',
          customerName: `${contact?.data?.firstName} ${contact?.data?.lastName}`,
        },
      },
    }),
    [contact?.data?.firstName, contact?.data?.lastName]
  );

  const checkRequestInitialValues = checkRequestConfig[checkRequestType as keyof typeof checkRequestConfig].initialValues;

  const handleSubmit = useCallback(
    async (data: ICreateCheckRequest) => {
      if (dealInfo && checkRequestType !== CheckRequestType.CheckRequest && dealInfo?.isBuyBack) {
        setShowAlert(true);
        return;
      }
      data.dealId = dealId;
      data.assetTag = dealResponse?.data?.assetTag || '';
      data.customerName = `${contact?.data?.firstName} ${contact.data?.lastName}`;
      data.relatedDeal = `${dealResponse?.data?.numericId} ${dealResponse?.data?.assetTag}`;
      data.requestedBy = userName || '';
      data.loanConsultant = contextValue?.lc || '';
      data.checkRequestType = Number(checkRequestType);
      data.reason = data.reason ?? '';
      data.requestComment = data.requestComment ?? '';
  
      setLoading(true);
      await submitCheckRequest({ ...data, IsApproved: isApproved }, workFlowErrorCb, isApproved).then(async (checkRequestResponse) => {
        if (checkRequestResponse?.isApprovalRequired) {
          setPendingSubmitData(data);
          setShowisApprovalRequiredAlert(true);
        } else {
          await mutate();
          dispatch(setLastCommentsRefresh(new Date().toISOString()));
        }
        setLoading(false);
      });
    },
    [dealId, dealResponse, contact, userName, contextValue, checkRequestType, dealInfo, isApproved]
  );

  return (
    <>
      <StyleOverride />
      <Modal
        hideBackdrop
        title={checkRequestType === CheckRequestType.CheckRequest ? 'Check Request' : 'Product Cancellation Checks'}
        dialogWidth="1200px"
        resetPosition={checkRequestModalPosition}
        open={!!checkRequestModalPosition}
        onClose={() => {
          setWorkFlowError('');
          handleClose();
        }}
      >
        <>
          {workFlowError ? (
            <div style={{ whiteSpace: 'pre-line' }}>{`${workFlowError}`}</div>
          ) : (
            <>
              <PhoenixGrid
                withHeader
                canNotCopy
                noButton
                className="check-request"
                height={checkReqList?.length ? '350px' : '150px'}
                pageSize={5}
                loading={!checkReqList}
                rows={mapGridIndexes(checkReqList)}
                columns={
                  checkRequestType === CheckRequestType.CheckRequest ? titleCheckRequestColumns : productCheckRequestColumns
                }
              />

              <Grid
                container
                spacing={12}
              >
                <Grid
                  item
                  md={12}
                >
                  <Formik
                    enableReinitialize
                    validateOnChange
                    initialValues={checkRequestInitialValues as ICreateCheckRequest}
                    validationSchema={checkRequestConfig[checkRequestType as keyof typeof checkRequestConfig].validation}
                    onSubmit={handleSubmit}
                  >
                    {(props) => (
                      <Form onSubmit={props.handleSubmit}>
                        <CheckRequestForm
                          dealInfo={dealInfo}
                          checkRequestType={checkRequestType}
                          contact={contact.data}
                          userName={userName}
                          handleClose={handleClose}
                          dealData={dealData}
                          payeeList={payeeList}
                          loading={loading}
                          resaonList={getOptions(reasonList, 'reason', 'reason')}
                          {...props}
                        />
                      </Form>
                    )}
                  </Formik>
                </Grid>
              </Grid>
            </>
          )}
        </>
      </Modal>
      <AlertModal
        hideCancel
        open={showAlert}
        title="Alert"
        subtitle="Buyback Deal. Cannot Process Product Cancel Check"
        buttonText="Ok"
        onClose={() => setShowAlert(false)}
        onConfirm={() => setShowAlert(false)}
      />
      <AlertModal
        open={showisApprovalRequiredAlert}
        title="Alert"
        subtitle="Check request requires Approval"
        buttonText="Ok"
        hideCancel={false}
        onClose={() => setShowisApprovalRequiredAlert(false)}
        onConfirm={async () => {
          setShowisApprovalRequiredAlert(false);
          if (pendingSubmitData) {
            setIsApproved(true);
            await submitCheckRequest({ ...pendingSubmitData, IsApproved: true }, workFlowErrorCb).then(async () => {
              await mutate();
              dispatch(setLastCommentsRefresh(new Date().toISOString()));
              setIsApproved(false);
              setPendingSubmitData(null);
            });
          }
        }}
      />
    </>
  );
};
